import React, {useEffect, useState} from "react";
import {Input} from "./input";
import {Button} from "./Button";
import TextArea from "./TextArea";
import VariantsList from "./VariantList";
import {productSchema} from "../utils/schema"

export default function ProductFormModal({open, onClose, onSubmit, product}) {
    const [image, setImage] = useState([]);
    const [alttext, setAlttext] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [details, setDetails] = useState("");
    const [isFeatured, setIsFeatured] = useState(false);
    const [price, setPrice] = useState("");
    const [sku, setSku] = useState("");
    const [variants, setVariants] = useState([]);
    const [options, setOptions] = useState([]);
    const [supplier, setSupplier] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [tags, setTags] = useState("");
    const [category, setCategory] = useState("");
    const [subcategory, setSubcategory] = useState("");
    const [priority, setPriority] = useState("");
    const [newProduct, setNewProduct] = useState(false);
    const [sizeChart, setSizeChart] = useState([]);
    const [sizeChartError, setSizeChartError] = useState("");
    const [errors, setErrors] = useState({});
    const [categorizations, setCategorizations] = useState([{category: "", subcategory: ""}]);
    const [googleDisplay, setGoogleDisplay] = useState(false);
    const [daysToHoliday, setDaysToHoliday] = useState(8);
    const [formError, setFormError] = useState("");
    const [googleProductCategory, setGoogleProductCategory] = useState("");
    const [productType, setProductType] = useState("");

    useEffect(() => {
        if (product) {
            setSizeChartError("");
            if (product?.sizeChart) {
                setSizeChart(JSON.stringify(product.sizeChart));
            }
            setImage(Array.isArray(product.image) ? product.image : product.image ? [product.image] : []);
            setAlttext(product.alttext || "");
            setTitle(product.title || "");
            setDescription(product.description || "");
            setDetails((product.details || []).join(", "));
            setIsFeatured(product.isFeatured || false);
            setPrice(product.price != null ? String(product.price) : "");
            setSku(product.sku || "");
            setVariants(product.variants || []);
            setSupplier(product.supplier || "");
            setQuantity(product.quantity != null ? product.quantity : "");
            setTags((product.tags || []).join(", "));
            setCategory(product.category || "");
            setSubcategory(product.subcategory || "");
            setOptions(
                product.options && Array.isArray(product.options)
                    ? product.options.map(opt => ({
                        name: opt?.name || "",
                        values: Array.isArray(opt?.values) ? opt.values : []
                    }))
                    : []
            );
            setPriority(product.priority || "");
            setNewProduct(product.newProduct || false);
            setGoogleProductCategory(product.googleProductCategory || "");
            setProductType(product.productType || "");
            setCategorizations(
                product.categorizations && product.categorizations.length > 0
                    ? product.categorizations.map(c => ({...c}))
                    : [{category: "", subcategory: ""}]
            );
            setGoogleDisplay(product.googleDisplay || false);
            setDaysToHoliday(product.daysToHoliday || 8);
        } else {
            setImage([]);
            setAlttext("");
            setTitle("");
            setDescription("");
            setDetails("");
            setIsFeatured(false);
            setPrice("");
            setSku("");
            setVariants([]);
            setSupplier("");
            setQuantity(0);
            setTags("");
            setCategory("");
            setSubcategory("");
            setGoogleProductCategory("");
            setProductType("");
            setOptions([]);
            setPriority(undefined);
            setNewProduct(false);
            setCategorizations([{category: "", subcategory: ""}]);
            setGoogleDisplay(false);
            setDaysToHoliday(8);
        }
    }, [product, open]);

    useEffect(() => {
        if (sizeChart && sizeChart.length > 0) {
            try {
                const parsed = JSON.parse(sizeChart);
                if (Array.isArray(parsed) && parsed.every(row => Array.isArray(row) && row.every(col => typeof col === "string"))) {
                    setSizeChartError("");
                } else {
                    setSizeChartError("Size chart must be a 2D array of strings.");
                }
            } catch (err) {
                setSizeChartError("Invalid JSON format.");
            }
        } else {
            setSizeChartError("");
        }
    }, [sizeChart]);

    const handleAddImage = () => setImage([...image, ""]);
    const handleImageChange = (index, value) => {
        const updated = [...image];
        updated[index] = value;
        setImage(updated);
    };
    const handleRemoveImage = index => setImage(image.filter((_, i) => i !== index));

    const handleSubmit = () => {
        let parsedSizeChart;
        if (sizeChart && sizeChart.length > 0) {
            try {
                parsedSizeChart = JSON.parse(sizeChart);
            } catch {
                parsedSizeChart = [];
            }
        } else {
            parsedSizeChart = undefined;
        }
        const data = {
            image: image.filter(Boolean),
            alttext,
            title,
            description,
            details: details.split(",").map(d => d.trim()).filter(Boolean),
            isFeatured,
            price: parseFloat(price),
            sku,
            variants: variants.map(v => ({
                ...v,
                price: parseFloat(v.price),
                quantity: parseInt(v.quantity, 10),
                image: (v.image || []).filter(Boolean),
            })),
            supplier,
            quantity: Number(quantity),
            tags: tags.split(",").map(t => t.trim()).filter(Boolean),
            category,
            subcategory,
            options,
            priority: priority ? parseInt(priority) : null,
            newProduct,
            sizeChart: parsedSizeChart || null,
            categorizations: categorizations.filter(c => c.category && c.subcategory),
            googleDisplay,
            daysToHoliday,
            googleProductCategory: googleProductCategory || null,
            productType: productType || null,
        };

        const parsed = productSchema.safeParse(data);
        if (!parsed.success) {
            const fieldErrors = {};
            let topLevelError = "";

            parsed.error.issues.forEach(err => {
                console.log('err:', err);
                if (err.path[0] === "variants") {
                    const idx = err.path[1];
                    const field = err.path[2];
                    if (!fieldErrors.variants) fieldErrors.variants = {};
                    if (!fieldErrors.variants[idx]) fieldErrors.variants[idx] = {};
                    fieldErrors.variants[idx][field] = err.message;
                } else if (typeof err.path[0] === "string") {
                    fieldErrors[err.path[0]] = err.message;
                } else if (err.path.length === 0) {
                    setFormError(err.message);
                }
            });

            setErrors(fieldErrors);
            setFormError(topLevelError);
            return;
        }

        setErrors({});
        onSubmit(data);
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto p-6 space-y-6">
                <h2 className="text-2xl font-bold">{product ? "Edit Product" : "Add Product"}</h2>

                <div>
                    <h3 className="text-lg font-semibold mb-2">Product Image</h3>
                    {image.map((img, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                            <Input
                                placeholder="Image URL"
                                value={img}
                                onChange={(e) => handleImageChange(index, e.target.value)}
                                className="w-full"
                            />
                            <Button variant="outline" onClick={() => handleRemoveImage(index)}>
                                Remove
                            </Button>
                        </div>
                    ))}
                    <Button variant="outline" onClick={handleAddImage}>
                        + Add Image
                    </Button>
                </div>

                <div className="flex flex-col gap-4 w-full">
                    <div className="flex flex-col">
                        <label className="mb-1 font-medium text-gray-700" htmlFor="alttext">
                            Alt Text
                        </label>
                        <Input
                            id="alttext"
                            name="alttext"
                            placeholder="Alt Text"
                            className="w-full"
                            value={alttext}
                            onChange={(e) => setAlttext(e.target.value)}
                        />
                        {errors.alttext && <p className="text-red-600 text-sm mt-1">{errors.alttext}</p>}
                    </div>

                    <div className="flex flex-col">
                        <label className="mb-1 font-medium text-gray-700" htmlFor="title">
                            Title
                        </label>
                        <Input
                            id="title"
                            name="title"
                            placeholder="Title"
                            className="w-full"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
                    </div>

                    <div className="flex flex-col">
                        <label className="mb-1 font-medium text-gray-700" htmlFor="description">
                            Description
                        </label>
                        <TextArea
                            id="description"
                            name="description"
                            placeholder="Description"
                            className="w-full"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        {errors.description && <p className="text-red-600 text-sm mt-1">{errors.description}</p>}
                    </div>

                    <div className="flex flex-col">
                        <label className="mb-1 font-medium text-gray-700" htmlFor="details">
                            Details
                        </label>
                        <Input
                            id="details"
                            name="details"
                            placeholder="Details (comma separated)"
                            className="w-full"
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                        />
                        {errors.details && <p className="text-red-600 text-sm mt-1">{errors.details}</p>}
                    </div>

                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={isFeatured}
                                   onChange={(e) => setIsFeatured(e.target.checked)}/>
                            Featured
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={newProduct}
                                   onChange={(e) => setNewProduct(e.target.checked)}/>
                            New Product
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700" htmlFor="sku">SKU</label>
                            <Input
                                id="sku"
                                name="sku"
                                placeholder="SKU"
                                className="w-full"
                                value={sku}
                                onChange={(e) => setSku(e.target.value)}
                            />
                            {errors.sku && <p className="text-red-600 text-sm mt-1">{errors.sku}</p>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700" htmlFor="supplier">Supplier</label>
                            <Input
                                id="supplier"
                                name="supplier"
                                placeholder="Supplier"
                                className="w-full"
                                value={supplier}
                                onChange={(e) => setSupplier(e.target.value)}
                            />
                            {errors.supplier && <p className="text-red-600 text-sm mt-1">{errors.supplier}</p>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700" htmlFor="tags">Tags</label>
                            <Input
                                id="tags"
                                name="tags"
                                placeholder="Tags (comma separated)"
                                className="w-full"
                                value={tags}
                                onChange={(e) => setTags(e.target.value)}
                            />
                            {errors.tags && <p className="text-red-600 text-sm mt-1">{errors.tags}</p>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700" htmlFor="googleDisplay">Feed to Google
                                Products</label>
                            <select
                                id="googleDisplay"
                                name="googleDisplay"
                                className="w-full border rounded p-2"
                                value={googleDisplay === true ? "true" : googleDisplay === false ? "false" : ""}
                                onChange={(e) => setGoogleDisplay(e.target.value === "true")}>
                                <option value="">-- Select --</option>
                                <option value="true">True</option>
                                <option value="false">False</option>
                            </select>
                            {errors.googleDisplay &&
                                <p className="text-red-600 text-sm mt-1">{errors.googleDisplay}</p>}
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700" htmlFor="googleProductCategory">
                                Google Product Category
                            </label>
                            <Input
                                id="googleProductCategory"
                                name="googleProductCategory"
                                placeholder="Apparel & Accessories > Clothing > Outerwear > Hoodies & Sweatshirts"
                                className="w-full"
                                value={googleProductCategory}
                                onChange={(e) => setGoogleProductCategory(e.target.value)}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Must match Google’s product taxonomy exactly.
                            </p>

                            <div className="mt-1 flex flex-wrap gap-2">
                                {[
                                    {label: "Shirts and Tops", id: "212"},
                                    {label: "Blanket", id: "1985"},
                                    {label: "Yoga Mat", id: "3640"},
                                    {label: "Jigsaw Puzzle", id: "2618"},
                                    {label: "Beach Towel", id: "4126"},
                                    {label: "Cat Toy", id: "5001"},
                                    {label: "Dog clothing", id: "5004"},
                                    {label: "Cat clothing", id: "5082"},
                                    {label: "Print Books", id: "543543"},
                                ].map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => setGoogleProductCategory(item.id)}
                                        className="text-blue-600 text-xs underline hover:text-blue-800"
                                    >
                                        {item.label} ({item.id})
                                    </button>
                                ))}
                            </div>

                            {errors.googleProductCategory && (
                                <p className="text-red-600 text-sm mt-1">{errors.googleProductCategory}</p>
                            )}
                        </div>


                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700" htmlFor="productType">
                                Product Type
                            </label>
                            <Input
                                id="productType"
                                name="productType"
                                placeholder="Blankets > Throws > Woven"
                                className="w-full"
                                value={productType}
                                onChange={(e) => setProductType(e.target.value)}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Your internal category structure (free-form).
                            </p>
                            {errors.productType && (
                                <p className="text-red-600 text-sm mt-1">{errors.productType}</p>
                            )}
                        </div>


                        <div className="flex flex-col gap-2">
                            <label className="font-medium mb-1">Categorizations</label>
                            {categorizations.map((cat, idx) => (
                                <div key={idx} className="flex gap-2 mb-2">
                                    <Input
                                        placeholder="Category"
                                        value={cat.category}
                                        onChange={e => {
                                            const updated = [...categorizations];
                                            updated[idx].category = e.target.value;
                                            setCategorizations(updated);
                                        }}
                                        className="w-1/2"
                                    />
                                    <Input
                                        placeholder="Subcategory"
                                        value={cat.subcategory}
                                        onChange={e => {
                                            const updated = [...categorizations];
                                            updated[idx].subcategory = e.target.value;
                                            setCategorizations(updated);
                                        }}
                                        className="w-1/2"
                                    />
                                    <Button variant="outline" onClick={() => {
                                        setCategorizations(categorizations.filter((_, i) => i !== idx));
                                    }}>Remove</Button>
                                </div>
                            ))}
                            {errors.categorizations && (
                                <p className="text-red-600 text-sm mb-2">{errors.categorizations}</p>
                            )}
                            <Button variant="outline" onClick={() => setCategorizations([...categorizations, {
                                category: "",
                                subcategory: ""
                            }])}>
                                + Add Categorization
                            </Button>
                        </div>

                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700"
                                   htmlFor="price">Price{product?.priceofstock && (` of stock ${product?.priceofstock}`)}</label>
                            <Input
                                id="price"
                                name="price"
                                placeholder="Price"
                                className="w-full"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                            />
                            {errors.price && <p className="text-red-600 text-sm mt-1">{errors.price}</p>}
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="font-medium mb-1">Options</label>
                            {options.map((opt, idx) => (
                                <div key={idx} className="flex gap-2 mb-2">
                                    <Input
                                        placeholder="Option Name (e.g. Color)"
                                        value={opt.name}
                                        onChange={e => {
                                            const updated = [...options];
                                            updated[idx].name = e.target.value;
                                            setOptions(updated);
                                        }}
                                        className="w-1/3"
                                    />
                                    <Input
                                        placeholder="Values (comma separated)"
                                        value={(opt.values || []).join(",")}
                                        onChange={e => {
                                            const updated = [...options];
                                            updated[idx].values = e.target.value.split(",").map(v => v.trim());
                                            setOptions(updated);
                                        }}
                                        className="w-2/3"
                                    />
                                    <Button
                                        variant="outline"
                                        onClick={() => setOptions(options.filter((_, i) => i !== idx))}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ))}
                            {errors.options && (
                                <p className="text-red-600 text-sm mb-2">{errors.options}</p>
                            )}
                            <Button variant="outline" onClick={() => setOptions([...options, {name: "", values: []}])}>
                                + Add Option
                            </Button>
                        </div>
                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700"
                                   htmlFor="price">Days To Holiday</label>
                            <Input
                                id="daysToHoliday"
                                name="daysToHoliday"
                                inputMode="numeric"
                                placeholder="Days To Holiday"
                                className="w-full"
                                value={daysToHoliday}
                                onChange={(e) => setDaysToHoliday(e.target.value)}
                            />
                            {errors.daysToHoliday &&
                                <p className="text-red-600 text-sm mt-1">{errors.daysToHoliday}</p>}
                        </div>
                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700" htmlFor="priority">Priority</label>
                            <Input
                                id="priority"
                                name="priority"
                                placeholder="Priority"
                                className="w-full"
                                value={priority}
                                onChange={(e) => setPriority(e.target.value)}
                            />
                            {errors.priority && <p className="text-red-600 text-sm mt-1">{errors.priority}</p>}
                        </div>
                        <div className="flex flex-col">
                            <label className="mb-1 font-medium text-gray-700" htmlFor="quantity">Quantity</label>
                            <Input
                                id="quantity"
                                name="quantity"
                                placeholder="Quantity"
                                className="w-full"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                            />
                            {errors.quantity && <p className="text-red-600 text-sm mt-1">{errors.quantity}</p>}
                        </div>
                    </div>


                    <div className="flex flex-col">
                        <label className="mb-1 font-medium text-gray-700">Size Chart</label>
                        <TextArea
                            name="sizeChart"
                            placeholder='Enter 2D array as JSON, e.g. [["S","M"],["Chest 34","Chest 36"]]'
                            value={sizeChart}
                            onChange={(e) => setSizeChart(e.target.value)}
                            className="w-full"
                            rows={5}
                        />
                        {sizeChartError && <p className="text-red-600 mt-1 text-sm">{sizeChartError}</p>}
                    </div>

                    <VariantsList variants={variants} setVariants={setVariants} errors={errors.variants || {}}/>

                    <div className="flex justify-end gap-4 pt-4">
                        {formError && (
                            <p className="text-red-600 text-sm mb-2">{formError}</p>
                        )}
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} disabled={!!sizeChartError} type="button">
                            {product ? "Update" : "Add"} Product
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}